/* eslint-disable import/no-extraneous-dependencies */
import React from 'react';
import { FormattedHTMLMessage, injectIntl, InjectedIntl } from 'react-intl';
import { produce } from 'immer';

import {
    VerificationService,
    StudentPersonalInfoViewModel,
    Organization,
    FormSelectChoice,
    StudentPersonalInfoResponse,
    Locale,
    ChangeLocaleComponent,
    CountryComponentWrapper,
    getAvailableLocaleChoices,
    handleEmailOnKeyDown,
    hasFailedInstantMatch,
    isFormFilled,
    LogoComponent,
    MarketConsentWrapper,
    RewardsRemainingComponent,
    setRef,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    VerificationStepsEnum,
    FirstNameComponent,
    HowDoesVerifyingWorkComponent,
    CollegeNameComponent,
    LastNameComponent,
    BirthDateComponent,
    EmailComponent,
    PhoneNumberComponent,
    FormFooterComponent,
} from '@sheerid/jslib';
import { sendPostMessage } from '../../lib/utils/browser';


interface StepStudentPersonalInfoComponentProps {
    intl: InjectedIntl;
    verificationService: VerificationService;
}

const StepStudentPersonalInfo = ({ intl, verificationService }: StepStudentPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as StudentPersonalInfoViewModel;
    const { fieldValidationErrors } = verificationService;
    const localeChoices: FormSelectChoice<Locale, string>[] = getAvailableLocaleChoices(verificationService.programTheme, intl);
    const defaultLocaleChoice: FormSelectChoice<Locale, string> = { value: 'en-US', label: 'English' };
    const verificationResponse = verificationService.verificationResponse as StudentPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

    const updateStudentViewModel = (key: keyof StudentPersonalInfoViewModel, value: any) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            (draft[key] as any) = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const updateLocale = (localeChoice: FormSelectChoice<Locale, string>) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            draft.localeChoice = localeChoice;
        });
        verificationService.updateLocale(
            nextState,
            verificationService.programTheme,
            verificationService.verificationResponse.segment,
        );
    };

    return (
        <div id="sid-step-student-personal-info" className="sid-l-container">
            { failedInstantMatch
                ? (
                    <div className="sid-header">
                        <div>
                            <LogoComponent verificationService={verificationService} />
                        </div>
                        <div className="sid-header__title">
                            <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Birthdate" />
                        </div>

                        <div className="sid-header__subtitle sid-header__subtitle--error">
                            <FormattedHTMLMessage
                                id="step.personalInfo.tryAgain.subtitle"
                                defaultMessage="Your full name and birthdate must match what is on your official school records."
                            />
                        </div>
                    </div>
                )
                : (
                    <div className="sid-header">
                        <div>
                            <LogoComponent verificationService={verificationService} />
                        </div>
                        <div className="sid-header__title">
                            <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Student-Only Offer" />
                        </div>

                        <div className="sid-header__subtitle sid-l-space-top-sm">
                            <FormattedHTMLMessage
                                id="preSubtitle.title"
                                defaultMessage="Google Pixel smartphone 10% discount¹"
                            />
                            <ul className="sid-header__subtitle--ul">
                                <li className="sid-header__subtitle--bullet">
                                    <FormattedHTMLMessage
                                        id="preSubtitle.bullet5"
                                        defaultMessage="Google Pixel 8"
                                    />
                                </li>
                                <li className="sid-header__subtitle--bullet">
                                    <FormattedHTMLMessage
                                        id="preSubtitle.bullet4"
                                        defaultMessage="Google Pixel 8 Pro"
                                    />
                                </li>
                                <li className="sid-header__subtitle--bullet">
                                    <FormattedHTMLMessage
                                        id="preSubtitle.bullet3"
                                        defaultMessage="Google Pixel 7a"
                                    />
                                </li>
                                <li className="sid-header__subtitle--bullet">
                                    <FormattedHTMLMessage
                                        id="preSubtitle.bullet2"
                                        defaultMessage="Google Pixel 7 Pro"
                                    />
                                </li>
                            </ul>
                        </div>

                        <div className="sid-header__subtitle sid-l-space-top-md">
                            <FormattedHTMLMessage
                                id="step.personalInfo.subtitle"
                                defaultMessage="Verify you're a student at a degree-granting school."
                            />
                        &nbsp;
                            <HowDoesVerifyingWorkComponent verificationService={verificationService} />
                            <RewardsRemainingComponent verificationService={verificationService} />
                        </div>
                    </div>
                )}

            {
                localeChoices.length > 1
                    ? (
                        <ChangeLocaleComponent
                            options={localeChoices}
                            value={viewModel.localeChoice || defaultLocaleChoice}
                            isErrored={false}
                            onChange={(localeChoice: FormSelectChoice<Locale, string>) => updateLocale(localeChoice)}
                        />
                    )
                    : null
            }

            <CountryComponentWrapper
                verificationService={verificationService}
                viewModel={viewModel}
                nextFocusField="organization"
            />

            <CollegeNameComponent
                value={(verificationService.viewModel as StudentPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <div className="sid-names">
                <LastNameComponent
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={
                        (newValue: string) => {
                            updateStudentViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />

                <FirstNameComponent
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={
                        (newValue: string) => {
                            updateStudentViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <BirthDateComponent
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue: string) => {
                    updateStudentViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <EmailComponent
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={(
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Personal or school email is fine"
                    />
                )}
                onChange={
                    (newValue: string) => {
                        updateStudentViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled
                    ? (
                        <PhoneNumberComponent
                            isRequired={!!verificationService.programTheme.smsLoopEnabled}
                            value={viewModel.phoneNumber}
                            isErrored={!!fieldValidationErrors.phoneNumber}
                            onChange={
                                (newValue: string) => {
                                    updateStudentViewModel('phoneNumber', newValue);
                                    updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                                }
                            }
                        />
                    )
                    : null
            }

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateStudentViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-wrapper__collect-info"
                        onClick={() => {
                            sendPostMessage('sheer_id_form_submitted', 'https://store.google.com');
                            submitForm(viewModel, verificationService, VerificationStepsEnum.collectStudentPersonalInfo);
                        }}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        aria-labelledby="verify-status-text"
                        ref={button => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            { failedInstantMatch
                                ? <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                                : <FormattedHTMLMessage id="verifyMyStudentStatus" defaultMessage="Verify My Student Status" />}
                        </span>
                    </button>
                </div>
            </div>

            <div className="sid-footer sid-l-space-top-lg">
                <FormattedHTMLMessage id="termsAndConditions" defaultMessage="¹Google Pixel pulls 10%. Promotional Code benefits are provided by Google Joint Company. It is only available for Japan\'s Google Store, and the following terms of use apply. You live in Japan and can only be used when the delivery address is in Japan. Target only 18 years old or older.<br><br>The promotion code should be used in Japan\'s Google Store by 23:59/31/2013 (Japan Time). If it is not available before it, expires and expired. SHEERID adapts to those who have identified qualifications of the study code.<br>Other promotional codes and benefits such as other simultaneous purchase bonus and discounts in the same period can not be used in combination." />
            </div>

            <FormFooterComponent verificationService={verificationService} />
        </div>
    );
};

export const StepStudentPersonalInfoComponent = injectIntl(StepStudentPersonalInfo);
